import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPaperPlane,
  faFileInvoice,
} from "@fortawesome/free-solid-svg-icons";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { app } from "../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import swal from "sweetalert";
import axios from "axios";

function InvoicesComp() {
  const db = getFirestore(app);
  const [clientsInvoices, setClientsInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState({}); // Track latest invoices per client

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);

        // Fetch all completed unpaid orders
        const q = query(
          collection(db, "Orders"),
          where("status", "==", "Completed"),
          where("paymentStatus", "==", "Unpaid")
        );
        const snapshot = await getDocs(q);
        const orders = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch all clients
        const clientsSnap = await getDocs(collection(db, "clients"));
        const clients = {};
        clientsSnap.forEach((c) => {
          clients[c.id] = c.data();
        });

        // Fetch invoices
        const invoicesSnap = await getDocs(collection(db, "invoices"));
        const invoicesData = {};

        invoicesSnap.forEach((inv) => {
          const data = inv.data();
          const clientId = data.clientId;
          if (!invoicesData[clientId]) {
            invoicesData[clientId] = data;
          } else {
            // Keep the most recent invoice
            if (
              data.createdAt?.toMillis() >
              invoicesData[clientId].createdAt?.toMillis()
            ) {
              invoicesData[clientId] = data;
            }
          }
        });
        setInvoices(invoicesData);

        // Group orders by client
        const grouped = {};
        orders.forEach((order) => {
          let clientId = order.clientId || order.newClientName;
          let clientName = "N/A";
          let clientPhone = order.clientPhone || "";

          if (order.orderType === "New Client" && order.newClientName) {
            clientName = order.newClientName;
          } else if (order.orderType === "Existing Client" && order.clientId) {
            clientName = clients[order.clientId]?.full_name || "N/A";
            clientPhone = clients[order.clientId]?.userphone || clientPhone;
          }

          if (!grouped[clientId]) {
            grouped[clientId] = {
              clientId,
              clientName,
              clientPhone,
              orders: [],
              totalAmount: 0,
            };
          }

          const amount = parseFloat(order.toPrice || 0);
          grouped[clientId].orders.push({ ...order, amount });
          grouped[clientId].totalAmount += amount;
        });

        setClientsInvoices(Object.values(grouped));
      } catch (err) {
        console.error("Error fetching invoices:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [db]);

  const filteredClients = clientsInvoices.filter((client) =>
    client.clientName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    const date = timestamp.toDate();
    return date.toLocaleString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSmsGreeting = (name) => {
    const trimmed = typeof name === "string" ? name.trim() : "";
    return trimmed ? `Hello ${trimmed},` : "Dear client,";
  };

  const handleSendInvoice = async (client) => {
    swal({
      title: "Send Invoice",
      text: `Send consolidated invoice to ${client.clientName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willSend) => {
      if (willSend) {
        try {
          // Create invoice in Firestore
          const invoiceDoc = await addDoc(collection(db, "invoices"), {
            clientId: client.clientId,
            clientName: client.clientName,
            clientPhone: client.clientPhone,
            orderIds: client.orders.map((o) => o.id),
            totalAmount: client.totalAmount,
            status: "sent",
            createdAt: serverTimestamp(),
          });

          // Encode clientId for URL
          const encodedClientId = encodeURIComponent(client.clientId);

          const greeting = getSmsGreeting(client.clientName);
          const message = `${greeting} your invoice for ${client.orders.length} deliveries is ready. Total: KES ${client.totalAmount.toFixed(
            2
          )}. View here: https://dropoff-mtaani.web.app/invoice/${encodedClientId}`;

          // Send SMS via new API
          const response = await axios.post(
            "https://us-central1-dropoff-mtaani.cloudfunctions.net/api/send-sms",
            {
              message,
              mobile: client.clientPhone,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (response.status === 200) {
            swal(
              "Sent!",
              `Invoice sent to ${client.clientName} successfully.`,
              "success"
            );
          } else swal("Error", "Failed to send invoice.", "error");
        } catch (err) {
          console.error("Error sending invoice:", err);
          swal("Error", "Failed to send invoice.", "error");
        }
      }
    });
  };

  return (
    <div className="dataComp p-4">
      <div className="dataCompTop d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold">Clients with Unpaid Deliveries</h5>
        <div className="searchContainer d-flex align-items-center">
          <FontAwesomeIcon icon={faSearch} className="searchIcon text-muted" />
          <input
            type="text"
            placeholder="Search client"
            value={searchTerm}
            className="form-control"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" />
          <p className="mt-2">Loading invoices...</p>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Client</th>
              <th>Unpaid Deliveries</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map((client) => {
              const lastInvoice = invoices[client.clientId];
              return (
                <tr key={client.clientId}>
                  <td>{client.clientName}</td>
                  <td>{client.orders.length}</td>
                  <td>KES {client.totalAmount.toFixed(2)}</td>
                  <td>
                    {lastInvoice?.status === "sent" ? (
                      <span className="text-success fw-bold">
                        Last sent on {formatDate(lastInvoice.createdAt)}
                      </span>
                    ) : (
                      <span className="text-danger fw-bold">Not Sent</span>
                    )}
                  </td>
                  <td className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleSendInvoice(client)}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} /> Send
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        window.open(`/invoice/${client.clientId}`, "_blank")
                      }
                    >
                      <FontAwesomeIcon icon={faFileInvoice} /> View
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default InvoicesComp;
